import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

export default function CartDrawer() {
  const { items, totalPrice, isOpen, closeCart, removeItem, updateQuantity, toast } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[200] transition-opacity duration-300"
        onClick={closeCart}
        aria-hidden
      />
      <aside
        className="cart-drawer-panel fixed top-0 right-0 bottom-0 w-full max-w-md bg-[var(--vyera-cream)] shadow-2xl z-[201] flex flex-col"
        aria-label="Cart"
      >
        <div className="p-6 border-b border-[var(--vyera-warm-grey)]/20 flex justify-between items-center">
          <h2 className="vyera-heading-md text-[var(--vyera-text-primary)]">Your bag</h2>
          <button
            type="button"
            onClick={closeCart}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[var(--vyera-black)]/5 transition-colors text-[var(--vyera-text-primary)]"
            aria-label="Close cart"
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <p className="vyera-body text-[var(--vyera-text-secondary)] py-12">
              Your bag is empty. Add something from the shop.
            </p>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li key={item.id} className="flex gap-4 pb-6 border-b border-[var(--vyera-warm-grey)]/20 last:border-0">
                  <div className="w-20 h-24 flex-shrink-0 bg-[var(--vyera-cream)] rounded overflow-hidden border border-[var(--vyera-warm-grey)]/30">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[var(--vyera-text-primary)] truncate">{item.name}{item.size ? ` (${item.size})` : ''}</p>
                    <p className="text-sm text-[var(--vyera-text-secondary)]">{item.price}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-7 h-7 rounded border border-[var(--vyera-warm-grey)]/50 text-[var(--vyera-text-primary)] hover:border-[var(--vyera-burgundy)] transition-colors"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm text-[var(--vyera-text-primary)]">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 rounded border border-[var(--vyera-warm-grey)]/50 text-[var(--vyera-text-primary)] hover:border-[var(--vyera-burgundy)] transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="text-[var(--vyera-text-secondary)] hover:text-[var(--vyera-burgundy)] text-sm transition-colors self-start"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {items.length > 0 && (
          <div className="p-6 border-t border-[var(--vyera-warm-grey)]/20 space-y-4">
            <div className="flex justify-between text-[var(--vyera-text-primary)]">
              <span>Subtotal</span>
              <span className="font-medium">{totalPrice}</span>
            </div>
            <button
              type="button"
              onClick={handleCheckout}
              className="w-full py-4 bg-[var(--vyera-black)] text-[var(--vyera-cream)] text-center font-medium hover:opacity-90 transition-opacity uppercase tracking-widest text-xs"
            >
              Checkout
            </button>
            <p className="text-xs text-[var(--vyera-text-secondary)] text-center">
              Shipping & taxes calculated at checkout.
            </p>
          </div>
        )}
      </aside>
      {toast && (
        <div
          className="cart-toast fixed bottom-8 left-1/2 -translate-x-1/2 z-[300] px-6 py-3 bg-[var(--vyera-black)] text-[var(--vyera-cream)] text-sm font-medium rounded-full shadow-lg"
          role="status"
        >
          {toast}
        </div>
      )}
    </>
  );
}
